import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';
import { ChipListField } from '@/features/store-settings';

import { inline422Message } from '../../model/templateErrors';
import type {
  ChatbotFaqIntent,
  ChatbotTemplateCatalogItem,
  ChatbotTemplatePlaceholder,
} from '../../model/types';
import { PlaceholderChips } from './PlaceholderChips';

export interface FaqEntryDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` = create a new entry. */
  intent: ChatbotFaqIntent | null;
  /** The catalogue's `family=FAQ_ANSWER` items for this locale — the answer picker's options. */
  faqAnswerItems: ChatbotTemplateCatalogItem[];
  faqAnswerKeyPattern: string;
  faqAnswerPlaceholders: ChatbotTemplatePlaceholder[];
  disabled?: boolean;
  saving?: boolean;
  /** Step 2 of the save (or the whole save, for an existing answer) — `PUT faq/{intentCode}`. Must
   * reject (not just resolve) on failure so the two-step retry logic in §13.8 can react to it. */
  onSave: (body: {
    intentCode: string;
    utterances: string[];
    keywords: string[];
    answerContentKey: string;
    enabled: boolean;
  }) => Promise<void>;
  /** Step 1 of a new answer's two-step save (templates doc §13.8) — `PUT templates/bot.faq.<slug>`. */
  onSaveNewAnswerText: (key: string, text: string) => Promise<void>;
}

const INTENT_CODE_PATTERN = /^[A-Z][A-Z0-9_]{2,40}$/;

function suggestedAnswerKey(intentCode: string): string {
  return `bot.faq.${intentCode.toLowerCase().replace(/_/g, '-')}`;
}

/** Create/edit one FAQ intent (templates doc §13.8) — the answer is either an existing `FAQ_ANSWER`
 * text or a brand-new one, saved in two resumable steps (the answer text, then the entry itself). */
export function FaqEntryDialog({
  open,
  onClose,
  intent,
  faqAnswerItems,
  faqAnswerKeyPattern,
  faqAnswerPlaceholders,
  disabled = false,
  saving = false,
  onSave,
  onSaveNewAnswerText,
}: FaqEntryDialogProps) {
  const { t } = useTranslation('chatbot');
  const isEdit = intent !== null;

  const [intentCode, setIntentCode] = useState('');
  const [utterances, setUtterances] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [answerContentKey, setAnswerContentKey] = useState<string | null>(null);
  const [newAnswerText, setNewAnswerText] = useState('');
  const [enabled, setEnabled] = useState(true);
  /** Shown near the answer field — a step-1 (new text) or existing-key save failure. */
  const [answerError, setAnswerError] = useState<string | null>(null);
  /** Shown as a banner — the step-2-only failure of a new answer's two-step save (§13.8). */
  const [partialSaveError, setPartialSaveError] = useState<string | null>(null);
  const [savingStep, setSavingStep] = useState(false);
  const newAnswerTextRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setIntentCode(intent?.intentCode ?? '');
    setUtterances(intent?.utterances ?? []);
    setKeywords(intent?.keywords ?? []);
    setAnswerContentKey(intent?.answerContentKey ?? null);
    setMode('existing');
    setNewAnswerText('');
    setEnabled(intent?.enabled ?? true);
    setAnswerError(null);
    setPartialSaveError(null);
  }, [open, intent]);

  const codeValid = INTENT_CODE_PATTERN.test(intentCode);
  const utterancesValid = utterances.length >= 3 && utterances.length <= 50;
  const keywordsValid = keywords.length <= 30;
  const derivedKey = suggestedAnswerKey(intentCode);
  const derivedKeyValid = new RegExp(faqAnswerKeyPattern).test(derivedKey);
  const existingWithDerivedKey = faqAnswerItems.find((item) => item.contentKey === derivedKey);
  const legacyKey =
    intent && !intent.answerContentKey.startsWith('bot.faq.') ? intent.answerContentKey : null;

  const canSubmit =
    codeValid &&
    utterancesValid &&
    keywordsValid &&
    (mode === 'existing'
      ? Boolean(answerContentKey)
      : Boolean(intentCode) && newAnswerText.trim() !== '');

  /** Placeholder chips insert at the caret, not appended at the end (templates doc §13.6). */
  const insertPlaceholderAtCaret = (token: string) => {
    const input = newAnswerTextRef.current;
    const start = input?.selectionStart ?? newAnswerText.length;
    const end = input?.selectionEnd ?? newAnswerText.length;
    setNewAnswerText((current) => current.slice(0, start) + token + current.slice(end));
    const caret = start + token.length;
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  const handleSubmit = async () => {
    setAnswerError(null);
    setPartialSaveError(null);

    // mode === 'existing': one call. mode === 'new': the two resumable steps of §13.8 — the button
    // spinner (`savingStep`) spans both.
    let key = answerContentKey ?? '';
    if (mode === 'new') {
      key = derivedKey;
      if (!existingWithDerivedKey) {
        setSavingStep(true);
        try {
          await onSaveNewAnswerText(key, newAnswerText);
        } catch (error) {
          setAnswerError(inline422Message(error as NormalizedApiError, t));
          setSavingStep(false);
          return;
        }
      }
    }

    setSavingStep(true);
    try {
      await onSave({ intentCode, utterances, keywords, answerContentKey: key, enabled });
    } catch (error) {
      if (mode === 'new') {
        // Step 1 already succeeded — only the retry (step 2) is needed now.
        setPartialSaveError(t('faqDialog.savedPartially'));
        setMode('existing');
        setAnswerContentKey(key);
      } else {
        setAnswerError(inline422Message(error as NormalizedApiError, t));
      }
    } finally {
      setSavingStep(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isEdit ? t('faqDialog.editTitle', { code: intent!.intentCode }) : t('faqDialog.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {partialSaveError ? <Alert severity="warning">{partialSaveError}</Alert> : null}
          <TextField
            label={t('faqDialog.intentCodeLabel')}
            value={intentCode}
            onChange={(event) => setIntentCode(event.target.value.toUpperCase())}
            disabled={disabled || isEdit}
            helperText={t('faqDialog.intentCodeHelper')}
            error={intentCode.length > 0 && !codeValid}
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
            fullWidth
          />
          <ChipListField
            label={t('faqDialog.utterancesLabel')}
            description={t('faqDialog.utterancesDescription')}
            value={utterances}
            onChange={setUtterances}
            disabled={disabled}
            placeholder={t('faqDialog.utterancesPlaceholder')}
          />
          <ChipListField
            label={t('faqDialog.keywordsLabel')}
            description={t('faqDialog.keywordsDescription')}
            value={keywords}
            onChange={setKeywords}
            disabled={disabled}
          />

          <Typography variant="subtitle2">{t('faqDialog.answerLabel')}</Typography>
          <RadioGroup
            aria-label={t('faqDialog.answerLabel')}
            value={mode}
            onChange={(_event, value) => setMode(value as 'existing' | 'new')}
          >
            <FormControlLabel
              value="existing"
              disabled={disabled}
              control={<Radio size="small" />}
              label={t('faqDialog.useExisting')}
            />
            {mode === 'existing' ? (
              <Box sx={{ pl: 4, pb: 1 }}>
                <Autocomplete
                  options={
                    legacyKey && !existingWithDerivedKey
                      ? [legacyKey, ...faqAnswerItems.map((i) => i.contentKey)]
                      : faqAnswerItems.map((i) => i.contentKey)
                  }
                  value={answerContentKey}
                  onChange={(_event, value) => setAnswerContentKey(value)}
                  disabled={disabled}
                  getOptionLabel={(key) => {
                    const item = faqAnswerItems.find((i) => i.contentKey === key);
                    const usedBy = item?.referencedByIntents.length
                      ? ` · ${t('faqDialog.usedBy', { intents: item.referencedByIntents.join(', ') })}`
                      : '';
                    return `${key}${usedBy}`;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          'aria-label': t('faqDialog.useExisting'),
                        },
                      }}
                    />
                  )}
                />
                {answerError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {answerError}
                  </Alert>
                ) : null}
              </Box>
            ) : null}
            {legacyKey && mode === 'existing' && answerContentKey === legacyKey ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 4, pb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('faqDialog.legacyKey')}
                </Typography>
                <Button size="small" onClick={() => setMode('new')} disabled={disabled}>
                  {t('faqDialog.moveToOwn')}
                </Button>
              </Stack>
            ) : null}

            <FormControlLabel
              value="new"
              disabled={disabled || !codeValid}
              control={<Radio size="small" />}
              label={t('faqDialog.writeNew')}
            />
            {mode === 'new' ? (
              <Box sx={{ pl: 4 }}>
                {!codeValid ? (
                  <Alert severity="info">{t('faqDialog.intentCodeFirst')}</Alert>
                ) : existingWithDerivedKey ? (
                  <Alert severity="info">
                    {t('faqDialog.newAnswerExists', { key: derivedKey })}
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {t('faqDialog.newAnswerKey', { key: derivedKey })}
                    </Typography>
                    {answerError ? <Alert severity="error">{answerError}</Alert> : null}
                    <TextField
                      label={t('faqDialog.newAnswerText')}
                      value={newAnswerText}
                      onChange={(event) => setNewAnswerText(event.target.value)}
                      inputRef={newAnswerTextRef}
                      disabled={disabled || !derivedKeyValid}
                      multiline
                      minRows={3}
                      fullWidth
                      slotProps={{ htmlInput: { maxLength: 4096 } }}
                      helperText={`${newAnswerText.length} / 4096`}
                    />
                    <PlaceholderChips
                      placeholders={faqAnswerPlaceholders}
                      onInsert={(placeholder) => insertPlaceholderAtCaret(`{{${placeholder}}}`)}
                      disabled={disabled}
                    />
                  </Stack>
                )}
              </Box>
            ) : null}
          </RadioGroup>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
              disabled={disabled}
              slotProps={{ input: { 'aria-label': t('faqDialog.activeLabel') } }}
            />
            <span>{t('faqDialog.activeLabel')}</span>
          </Stack>
          {!utterancesValid && utterances.length > 0 ? (
            <Alert severity="warning">{t('faqDialog.utterancesRangeWarning')}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('editor.cancel')}</Button>
        <Button
          variant="contained"
          disabled={disabled || !canSubmit || saving || savingStep}
          onClick={() => void handleSubmit()}
        >
          {saving || savingStep ? t('editor.saving') : t('editor.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
